import {
  Weblog,
  ShowOptions,
  TaskOptions,
  CustomOptions,
  BaseOptions
} from '@ks/weblogger/lib/log.browser';
import { Weblog as HybridWeblog } from '@ks/weblogger/lib/log.hybrid';
import AutoPV from '@ks/weblogger/lib/plugins/autopv';
import {
  performanceTiming,
  addMonitor,
  removeMonitor,
  supportsPushState,
  pagehideEventName
} from './utils';
import { getUrlMapFn } from './url-map';
import {
  AioWeblogConfig,
  FunctionReturnVoid,
  AioWeblogCollectorArgs,
  AioWeblogPVCollector,
  AioWeblogCollector,
  SupportShowTypes
} from './types';

export type AioWeblogHookMethod = (weblog: AioWeblog) => void;

class AioWeblog {
  /** 避免被外部实例化 */
  private constructor() {
    // ...
    this.urlChangeBindThis = this.urlChange.bind(this);
    this.visibilityChangeBindThis = this.visibilityChange.bind(this);
    this.destroyBindThis = this.destroy.bind(this);
  }

  /** 单例的实例 */
  private static weblogInstance: AioWeblog;

  /** 单例获取实例的方法 */
  public static getInstance() {
    if (!AioWeblog.weblogInstance) {
      AioWeblog.weblogInstance = new AioWeblog();
    }

    return AioWeblog.weblogInstance;
  }

  /** 开始时间，new Date().valueOf() */
  private beginTime: number;

  /** 暂存原始的pushState */
  private oldPushState: History['pushState'];

  /** 暂存原始的replaceState */
  private oldReplaceState: History['replaceState'];

  /** 组件 Weblog 的实例 */
  private weblog: Weblog;

  private isHybrid: boolean;

  /**
   * 初始化Weblog组件，参数可参照 https://component.corp.kuaishou.com/docs/weblogger/views/docs/config.html#%E5%88%9D%E5%A7%8B%E5%8C%96%E5%8F%82%E6%95%B0%E9%85%8D%E7%BD%AE
   * @param sdkConfig 初始化参数配置
   * @param baseOption 埋点基础信息配置
   */
  init(sdkConfig: AioWeblogConfig, baseOption: BaseOptions) {
    const { routes = {}, plugins, ...restConfigs } = sdkConfig;

    // eslint-disable-next-line no-unused-expressions
    plugins?.push(new AutoPV());
    let weblogConstructor = Weblog;
    if (window.location.pathname === '/') {
      weblogConstructor = HybridWeblog;
      this.isHybrid = true;
    }
    this.weblog = new weblogConstructor(
      {
        ...restConfigs,
        plugins,
        urlMap: getUrlMapFn(routes)
      },
      baseOption
    );

    // 初始化beginTime
    this.beginTime =
      (performanceTiming && performanceTiming.fetchStart) ||
      new Date().valueOf();

    // 首次PV
    this.collectPV({
      type: 'enter',
      beginTime: this.beginTime
    });

    // 记录原始方法
    this.oldPushState = window.history.pushState;
    this.oldReplaceState = window.history.replaceState;
    // 代理原始方法
    window.history.pushState = this.proxyPushState.bind(this);
    window.history.replaceState = this.proxyReplaceState.bind(this);
    // 添加监听事件
    addMonitor(
      window,
      supportsPushState() ? 'popstate' : 'hashchange',
      this.urlChangeBindThis
    );
    addMonitor(document, 'visibilitychange', this.visibilityChangeBindThis);
    addMonitor(window, pagehideEventName, this.destroyBindThis);
  }

  /** 获取原始weblog实例，可通过该实例调用原始方法 */
  getSourceWeblog(): Weblog {
    return this.weblog;
  }

  private destroyBindThis: FunctionReturnVoid;

  /** 销毁方法 */
  private destroy() {
    // 批量发送事件
    this.doWeblogCollect();
    this.weblog.flush();
    this.weblog.sendImmediately('PV', {
      type: 'leave',
      beginTime: this.beginTime
    });

    // 解绑事件、还原方法
    removeMonitor(
      window,
      supportsPushState() ? 'popstate' : 'hashchange',
      this.urlChangeBindThis
    );
    removeMonitor(document, 'visibilitychange', this.visibilityChangeBindThis);
    removeMonitor(window, pagehideEventName, this.destroyBindThis);
    window.history.pushState = this.oldPushState;
    window.history.replaceState = this.oldReplaceState;
  }

  /** 页面自定义数据是否初始化好了 */
  private pageParamsInitialized: boolean;

  /** 需要自定义页面参数的时间队列 */
  private collectArgsQueue: AioWeblogCollectorArgs[];

  /** 进入页面后初始化数据 */
  initPageParams(params?: Record<string, unknown>) {
    this.weblog.updateCurrentUrlPackage({
      params,
      page: window.location.href
    });

    this.pageParamsInitialized = true;

    // 使用设置好的数据，来收集事件
    this.doWeblogCollect();
  }

  private visibilityChangeBindThis: FunctionReturnVoid;

  private visibilityChange() {
    if (document.visibilityState === 'hidden') {
      this.collectPV({
        type: 'hidden',
        beginTime: this.beginTime
      });
    }

    if (document.visibilityState === 'visible') {
      this.collectPV({
        type: 'visible',
        beginTime: this.beginTime
      });
    }
  }

  private urlChangeBindThis: FunctionReturnVoid;

  private urlChange() {
    setTimeout(() => {
      /* 处理上一个页面的数据上报 */
      this.collectPV({
        type: 'leave',
        beginTime: this.beginTime
      });

      // 如果在结束时还未获取初始化数据完成，则忽略这部分数据，直接收集上报
      if (!this.pageParamsInitialized) {
        this.doWeblogCollect();
      }

      // 设置下一个页面的page，不包含自定义数据
      this.weblog.updateCurrentUrlPackage(window.location.href);

      // 初始化beginTime
      this.beginTime = new Date().valueOf();
      // 切换页面，page数据变成非初始化状态
      this.pageParamsInitialized = false;
      this.collectPV({
        type: 'enter',
        beginTime: this.beginTime
      });
    }, 0);
  }

  private proxyPushState: History['pushState'] = (data, title, url) => {
    this.urlChange();
    return this.oldPushState.call(window.history, data, title, url);
  };

  private proxyReplaceState: History['replaceState'] = (data, title, url) => {
    this.urlChange();
    return this.oldReplaceState.call(window.history, data, title, url);
  };

  /** autoPV收集事件 */
  private collectPV: AioWeblogPVCollector = (
    options,
    params,
    contentPackage
  ) => {
    this.collect('PV', options, params, contentPackage);
  };

  get isHybridWeblog() {
    return this.isHybrid;
  }
  /** 收集事件 */
  collect: AioWeblogCollector = (type, options, params, contentPackage) => {
    if (this.pageParamsInitialized) {
      if (typeof options === 'string') {
        this.weblog.collect(type, options, params, contentPackage);
      } else {
        this.weblog.collect(type, options);
      }
      return;
    }

    // 初始化
    if (!this.collectArgsQueue) {
      this.collectArgsQueue = [];
    }

    this.collectArgsQueue.push([type, options, params, contentPackage]);
  };

  /** 执行队列中的collect事件 */
  doWeblogCollect() {
    if (!this.collectArgsQueue) return;

    this.collectArgsQueue.forEach(([arg1, arg2, ...restArgs]) => {
      if (typeof arg2 === 'string') {
        this.weblog.collect(arg1, arg2, ...restArgs);
      } else {
        this.weblog.collect(arg1, arg2);
      }
    });

    // 执行上报
    this.weblog.flush();

    // 清空队列
    this.collectArgsQueue = [];
  }
  sendTask(
    /** Task事件类型 */
    type: any,
    /** 事件埋点id */
    actionId: TaskOptions['action'],
    /** 其他参数 */
    params?: TaskOptions['params'],
    /** 一个埋点同学都不清楚的参数 */
    contentPackage?: TaskOptions['contentPackage']
  ) {
    this.weblog.sendImmediately(type, {
      action: actionId,
      params,
      contentPackage
    });
  }
  sendShow(
    /** 事件埋点id */
    actionId: string,
    /** 其他参数 */
    params: Record<string, unknown> = {},
    /** 一个埋点同学都不清楚的参数 */
    contentPackage?: ShowOptions['contentPackage']
  ) {
    this.weblog.sendImmediately('SHOW', {
      action: actionId,
      params,
      contentPackage
    });
  }
  sendClick(
    /** 事件埋点id */
    actionId: string,
    /** 其他参数 */
    params: Record<string, unknown> = {},
    /** 一个埋点同学都不清楚的参数 */
    contentPackage?: string
  ) {
    this.weblog.sendImmediately('CLICK', {
      action: actionId,
      params,
      contentPackage
    });
  }

  fmp(timestamp?: number) {
    if (this.weblog?.plugins?.radar?.fmp) {
      this.weblog.plugins.radar.fmp(timestamp);
    }
  }

  customStage(key: string, timestamp: number) {
    if (this.weblog?.plugins?.radar?.customStage) {
      this.weblog.plugins.radar.customStage(key, { timestamp });
    }
  }

  actionStart(params: { name: string; extraInfo?: Record<string, any> }) {
    if (this.weblog.plugins?.radar?.action?.start) {
      const { name, extraInfo } = params;
      this.weblog.plugins.radar.action.start({
        name, // 关键动作名
        extra_info: extraInfo // 【可选】需要是对象
      });
    }
  }

  actionEnd(params: {
    name: string;
    extraInfo?: Record<string, any>;
    resultType?: string;
  }) {
    if (this.weblog.plugins?.radar?.action?.end) {
      const { name, resultType, extraInfo } = params;
      this.weblog.plugins.radar.action.end({
        name, // 关键动作名
        extra_info: extraInfo, // 【可选】需要是对象
        result_type: resultType // 【可选】 事件结果
      });
    }
  }
}

const aioWeblog = AioWeblog.getInstance();
export default aioWeblog;

/** 事件回调包装高阶函数 */
export function trackClick<F extends (e: Event) => any>(
  /** 回调函数 */
  func: F,
  /** 事件埋点id */
  actionId: string,
  /** 其他参数 */
  params: Record<string, unknown> = {},
  /** 一个埋点同学都不清楚的参数 */
  contentPackage?: string
): F {
  const newFunc = (e: Event) => {
    const res = func.call(e && e.target, e);
    aioWeblog.collect('CLICK', {
      action: actionId,
      params,
      contentPackage
    });
    return res;
  };

  return newFunc as F;
}

/** 手动发送点击事件统计 */
export function sendClick(
  /** 事件埋点id */
  actionId: string,
  /** 其他参数 */
  params: Record<string, unknown> = {},
  /** 一个埋点同学都不清楚的参数 */
  contentPackage?: string
) {
  if (aioWeblog.isHybridWeblog) {
    aioWeblog.sendClick(actionId, params, contentPackage);
    return;
  }
  aioWeblog.collect('CLICK', {
    action: actionId,
    params,
    contentPackage
  });
  aioWeblog.doWeblogCollect();
}

/** 手动发送Task事件统计 */
export function sendTask(
  /** Task事件类型 */
  type: any,
  /** 事件埋点id */
  actionId: TaskOptions['action'],
  /** 其他参数 */
  params?: TaskOptions['params'],
  /** 一个埋点同学都不清楚的参数 */
  contentPackage?: TaskOptions['contentPackage']
) {
  if (aioWeblog.isHybridWeblog) {
    aioWeblog.sendTask(type, actionId, params, contentPackage);
    return;
  }
  aioWeblog.collect(type, {
    action: actionId,
    params,
    contentPackage
  });
  aioWeblog.doWeblogCollect();
}

/** 手动发送Show事件统计 */
export function sendShow(
  /** Task事件类型 */
  type: SupportShowTypes,
  /** 事件埋点id */
  actionId: ShowOptions['action'],
  /** 其他参数 */
  params: ShowOptions['params'],
  /** 一个埋点同学都不清楚的参数 */
  contentPackage?: ShowOptions['contentPackage']
) {
  if (aioWeblog.isHybridWeblog) {
    aioWeblog.sendShow(actionId, params, contentPackage);
    return;
  }
  aioWeblog.collect(type, {
    action: actionId,
    params,
    contentPackage
  });
  aioWeblog.doWeblogCollect();
}

/** 手动发送Show事件统计 */
export function sendCustom(options: CustomOptions) {
  aioWeblog.collect('CUSTOM', options);
  aioWeblog.doWeblogCollect();
}

export * from './url-map';

/**
 * ====== global.d.ts ======
 * 在`apps/app`中引用weblog，build时，weblog/global.d.ts文件无法被TS感知，因此直接放到index中
 */
declare global {
  interface Window {
    Element: Element;
    mozRequestAnimationFrame(callback: FrameRequestCallback): number;
    mozCancelAnimationFrame(handle: number): void;
    ActiveXObject: Record<string, unknown>;
    attachEvent(
      type: string,
      listener: EventListenerOrEventListenerObject
    ): void;
    detachEvent(
      type: string,
      listener: EventListenerOrEventListenerObject
    ): void;
  }
  interface Element {
    attachEvent(
      type: string,
      listener: EventListenerOrEventListenerObject
    ): void;
    detachEvent(
      type: string,
      listener: EventListenerOrEventListenerObject
    ): void;
  }
}
